<template>
  <div class="activity-risk-category">
    <v-row no-gutters class="search-container align-center pb-6">
      <v-col cols="4">
        <v-select
          id="selectedActivityType"
          v-model="selectedActivityType"
          class="excursion-selection"
          :items="listActivityTypes"
          item-title="name"
          item-value="name"
          label="Select an Activity Type"
          testing-id="selected-activity-type"
          variant="solo"
          density="compact"
          color="primary"
          single-line
          hide-details
          return-object
          clearable
          clear-icon="mdi-close"
          :disabled="!formControlEnabled || isExcursionInitiatedFromPcgNextGen"
          @update:model-value="resetSelectedClass"
        >
          <template #item="data">
            <v-list-item
              v-bind="data.props"
              :id="`activity-risk-category--activity-type--option-${String(
                data.props.title
              ).replaceAll(' ', '')}`"
            >
              {{ data.item.name }}
            </v-list-item>
          </template>
        </v-select>
      </v-col>
      <v-col cols="1" class="d-flex justify-center ma-0">
        <v-icon size="large">mdi-chevron-right</v-icon>
      </v-col>
      <v-col cols="4">
        <v-select
          id="activity-risk-category--activity-class--selection"
          v-model="selectedActivityClass"
          class="excursion-selection"
          :items="listActivityClasses"
          item-title="name"
          item-value="name"
          :label="placeholderActivityRiskClass"
          variant="solo"
          color="primary"
          density="compact"
          hide-details
          single-line
          clearable
          return-object
          clear-icon="mdi-close"
          :disabled="!formControlEnabled || isExcursionInitiatedFromPcgNextGen"
          :menu-props="{
            maxHeight: 400
          }"
        >
          <template #item="data">
            <v-list-item
              v-bind="data.props"
              :id="`activity-risk-category--activity-class--option-${String(
                data.item.title
              ).replaceAll(' ', '')}`"
            >
              {{ data.item.name }}
            </v-list-item>
          </template>
        </v-select>
      </v-col>
    </v-row>
    <stb-multi-select
      id="selectActivityRisk"
      :select-options="filterActivityRisks(selectedActivityType, selectedActivityClass)"
      :placeholder="placeholder"
      :disabled="!formControlEnabled || isExcursionInitiatedFromPcgNextGen"
      :get-disabled-state="getDisabledState"
      @stb-multi-select-change="onMultiSelectChange"
    />
    <div v-if="0 < selectedActivityClasses.length" class="activity-category-class">
      <div v-for="(riskType, tindex) in selectedActivityClasses[0].types" :key="tindex">
        <div
          v-for="riskClass in riskType.classes"
          :key="setKey(riskClass)"
          class="sub-category-section"
        >
          <v-row no-gutters class="align-center">
            <v-col>
              <div class="name">{{ riskClass.name }}</div>
            </v-col>
            <v-col v-if="checklistAndPolicyDisplayed" class="d-flex justify-end">
              <complete-checklist
                ref="completeChecklist"
                :category-name="selectedActivityClasses[0].name"
                :risk-type-name="riskType.name"
                :risk-class-name="riskClass.name"
                :associated-checklist-completed="getTicked(riskClass)"
                :form-control-enabled="formControlEnabled"
                :element-disabled="
                  (riskClass.risks &&
                    elementDisabled &&
                    riskType.name === checklistFetchActionRiskTypeName &&
                    riskClass.name === checklistFetchActionRiskClassName) ||
                  !riskClass.risks
                "
                :checklist-fetch-action-category-name="selectedActivityClasses[0].name"
                :checklist-fetch-action-risk-type-name="checklistFetchActionRiskTypeName"
                :checklist-fetch-action-risk-class-name="checklistFetchActionRiskClassName"
                :checklist-fetch-action-in-progress="checklistFetchActionInProgress"
                :save-draft="saveDraft"
              />
              <view-policy
                :category-name="selectedActivityClasses[0].name"
                :risk-type-name="riskType.name"
                :risk-class-name="riskClass.name"
                :element-disabled="!riskClass.risks"
                :activity-supervisor-scroll-displayed="true"
              />
            </v-col>
          </v-row>
          <div v-if="!riskClass.risks">
            <v-row>
              <v-col cols="11">
                <v-select
                  id="changeActivityPreSelectedRisk"
                  class="type-dropdown risk-class-select excursion-selection empty-risk-selection"
                  :items="listActivityRisks('', riskClass.name, riskType.name)"
                  item-disabled="disabled"
                  color="primary"
                  variant="solo"
                  density="compact"
                  testing-id="list-risk"
                  placeholder="Select a risk"
                  hide-details
                  @update:model-value="
                    changeActivitySelectedRiskId(riskClass.name, riskType.name, {}, $event)
                  "
                >
                  <template #item="data">
                    <v-list-item
                      v-if="data.item.raw.type !== 'subheader'"
                      v-bind="data.props"
                      :id="`activity-risk-category--activity-selected-risk--option-${String(
                        data.item.title
                      ).replaceAll(' ', '')}`"
                      :disabled="getDisabledState(data.item)"
                      active-class="active-risk-category-activity"
                    />
                  </template>
                </v-select>
              </v-col>
              <v-col cols="1" class="justify-center">
                <div>
                  <v-icon
                    :id="getActivityRiskDeleteId(riskType, riskClass, null)"
                    :disabled="!formControlEnabled || isExcursionInitiatedFromPcgNextGen"
                    size="large"
                    @click="displayDeleteRiskConfirm(riskType, riskClass, null)"
                  >
                    mdi-delete
                  </v-icon>
                </div>
              </v-col>
            </v-row>
          </div>
          <div v-if="riskClass.risks && 0 < riskClass.risks.length">
            <v-row v-for="(selectedRisk, index) in riskClass.risks" :key="index" dense>
              <v-col cols="11">
                <v-select
                  :id="getActivityRiskId(riskType, riskClass, selectedRisk)"
                  class="type-dropdown risk-class-select excursion-selection"
                  :items="listActivityRisks(selectedRisk.name, riskClass.name, riskType.name)"
                  color="primary"
                  variant="solo"
                  density="compact"
                  hide-details
                  :model-value="selectedRisk.name"
                  testing-id="list-risk"
                  :disabled="!formControlEnabled || isExcursionInitiatedFromPcgNextGen"
                  @update:model-value="
                    changeActivitySelectedRiskId(
                      riskClass.name,
                      riskType.name,
                      selectedRisk,
                      $event
                    )
                  "
                >
                  <template #item="data">
                    <v-list-item
                      v-if="data.item.raw.type !== 'subheader'"
                      v-bind="data.props"
                      :id="`activity-risk-category--activity-selected-risk--option-${String(
                        data.item.name
                      ).replaceAll(' ', '')}`"
                      :disabled="getDisabledState(data.item)"
                      active-class="active-risk-category-activity"
                      :class="{
                        'disabled-risk': getDisabledState(data.item)
                      }"
                    />
                  </template>
                </v-select>
              </v-col>
              <v-col cols="1" class="d-flex justify-center">
                <v-icon
                  :id="getActivityRiskDeleteId(riskType, riskClass, selectedRisk)"
                  :disabled="!formControlEnabled || isExcursionInitiatedFromPcgNextGen"
                  size="large"
                  @click="displayDeleteRiskConfirm(riskType, riskClass, selectedRisk)"
                >
                  mdi-delete
                </v-icon>
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
    </div>
    <cs-form-dialog
      id="dlg-activity-risk-category"
      v-model="deleteActivityRiskDisplayed"
      body-id="dlg-body-activity-risk-category"
      heading="Delete Risk Category Type"
      :primary-action="{
        label: 'Delete',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="removeActivitySelectedRiskId"
      @secondary1-click="deleteActivityRiskDisplayed = false"
      @update:model-value="deleteActivityRiskDisplayed = false"
    >
      <template #cs-form-dialog-content>Are you sure?</template>
    </cs-form-dialog>
  </div>
</template>

<script>
import * as types from '@/store/mutationTypes';
import riskCategoryLabel from '../lib/const/riskCategoryLabel';
import CompleteChecklist from './CompleteChecklist';
import { map, find, filter, get } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import StbMultiSelect from './StbMultiSelect';

export default {
  name: 'ActivityRiskCategory',
  components: {
    CompleteChecklist,
    StbMultiSelect
  },
  props: {
    formControlEnabled: {
      type: Boolean,
      required: false,
      default: false
    },
    checklistAndPolicyDisplayed: Boolean,
    isExcursionInitiatedFromPcgNextGen: {
      type: Boolean,
      default: false
    },
    saveDraft: {
      type: Function,
      default: () => {}
    }
  },
  emits: ['changeActivityDone'],
  data() {
    return {
      checklistFetchActionRiskTypeName: '',
      checklistFetchActionRiskClassName: '',
      checklistFetchActionInProgress: false,
      checklistFetchActionCount: 0,
      checklistFetchActionDoneCount: 0,
      elementDisabled: true,
      currentSelectedType: {},
      currentSelectedClass: {},
      currentSelectedRisk: {},
      deleteActivityRiskDisplayed: false
    };
  },
  computed: {
    ...mapState({
      excursionRiskCategories: (state) => state.excursions.riskCategoriesOptions
    }),
    ...mapGetters({
      isRiskCategoriesOptionsLoaded: 'excursions/isRiskCategoriesOptionsLoaded'
    }),
    placeholder() {
      let placeHolder = 'Add Activity Type';
      if (!!this.selectedActivityType && !!this.selectedActivityClass) {
        placeHolder = `Select ${this.selectedActivityClass} Type`;
      }
      return placeHolder;
    },
    selectedActivityType: {
      get() {
        return this.$store.state.common.selectedActivityTypeFilter;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_SELECTED_ACTIVITY_TYPE_FILTER, value);
      }
    },
    selectedActivityClass: {
      get() {
        return this.$store.state.common.selectedActivityClassFilter;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_SELECTED_ACTIVITY_CLASS_FILTER, value);
      }
    },
    activityRiskCategories() {
      return this.excursionRiskCategories?.activities || [];
    },
    allActivityRisks() {
      return this.$store.getters['common/getAllActivityRisks'];
    },
    filterActivityRisks() {
      return (type, riskClass) => {
        const typeName = type || '';
        const className = riskClass || '';

        if (!typeName || !className) {
          return this.activityRiskCategories.flatMap((riskCategory) => {
            if (!riskCategory || !riskCategory.subCategories) return [];
            return this.flattenActivityRiskCategory(riskCategory.subCategories, riskCategory.name);
          });
        }

        const category = find(this.activityRiskCategories, { name: typeName });
        if (!category || !category.subCategories) return [];

        const filteredActivitySubRisks = filter(category.subCategories, { name: className });
        return this.flattenActivityRiskCategory(filteredActivitySubRisks, category.name);
      };
    },
    selectedActivityClasses() {
      return this.$store.getters['common/getSelectedActivityClasses'];
    },
    listActivityClasses() {
      const selectedActivityClasses = find(this.activityRiskCategories, {
        name: this.selectedActivityType
      });

      if (selectedActivityClasses && selectedActivityClasses.subCategories) {
        return map(selectedActivityClasses.subCategories, 'name');
      }
      return [];
    },
    listActivityTypes() {
      return map(this.activityRiskCategories, 'name');
    },
    placeholderActivityRiskClass() {
      if (this.selectedActivityType) {
        return `Select ${this.selectedActivityType} Type`;
      }

      return '';
    },
    getDisabledState() {
      return (item) => {
        if (!item?.raw) return false;
        const activityCategory = item.raw.activityCategory;
        const activitySubRiskType = item.raw.subRiskType;
        const riskType = item.raw.value;

        if (!this.selectedActivityClasses || !this.selectedActivityClasses.length) return false;
        const allTypes = this.selectedActivityClasses.flatMap(
          (activityClass) => activityClass.types || []
        );

        const matchingType = find(
          allTypes,
          (riskClass) => riskClass && riskClass.name === activityCategory
        );
        if (!matchingType || !matchingType.classes) return false;

        const matchingClass = matchingType.classes.find(
          (riskItem) => riskItem && riskItem.name === activitySubRiskType
        );
        if (!matchingClass || !matchingClass.risks) return false;

        return matchingClass.risks.some((risk) => risk && risk.name === riskType);
      };
    }
  },
  methods: {
    listActivityRisks(selectedSubRisk, subCategoryName, activityCategoryName) {
      if (!this.isRiskCategoriesOptionsLoaded) return [];

      const riskClass = this.activityRiskCategories.find((r) => r.name === activityCategoryName);

      if (!riskClass || !riskClass.subCategories) return [];

      const result = filter(riskClass.subCategories, { name: subCategoryName });

      if (selectedSubRisk) {
        if (result[0].options.findIndex((r) => r === selectedSubRisk) === -1) {
          return [selectedSubRisk];
        }
      }

      // Prevent "Complete Checklist" button from being disabled when the activity risk loads
      if (!this.checklistFetchActionInProgress) {
        this.elementDisabled = false;
      }

      return this.flattenActivityRiskCategory(result, riskClass.name);
    },
    getActivityRiskFromAllRisks(className, typeName, selectedRisk) {
      const riskClass = find(this.activityRiskCategories, { name: typeName });

      if (riskClass && riskClass.subCategories) {
        const activitySubCategory = find(riskClass.subCategories, { name: className });
        return activitySubCategory.options.find((risk) => risk === selectedRisk);
      }

      return undefined;
    },
    flattenActivityRiskCategory(subcategories, categoryName) {
      if (!Array.isArray(subcategories)) return [];
      return subcategories.flatMap((item) => {
        const itemOptions = get(item, 'options', []) || [];
        const subRiskOptions = itemOptions.map((option) => ({
          title: option,
          subRiskType: item.name,
          value: option,
          activityCategory: categoryName
        }));

        return [{ type: 'subheader', title: item.name, disabled: true }, ...subRiskOptions];
      });
    },
    onMultiSelectChange(selectedRisk) {
      const riskCategoryName = selectedRisk.activityCategory;
      const subRiskCategoryName = selectedRisk.subRiskType;
      const risk = { name: selectedRisk.value };

      this.$store.dispatch('common/changeActivitySelectRisk', {
        risk,
        riskCategoryName,
        subRiskCategoryName
      });

      this.changeActivityRiskClassChecklist(riskCategoryName, subRiskCategoryName);
      this.$emit('changeActivityDone');
    },
    changeActivitySelectedRiskId(className, typeName, selectedRisk, event) {
      const localSelectedRisk = selectedRisk;
      const currentSelected = this.getActivityRiskFromAllRisks(className, typeName, event);

      this.$store.dispatch('common/setExcursionSelectedCategoryTypeClassRisk', {
        categoryName: riskCategoryLabel.ACTIVITIES,
        typeName,
        className,
        oldRiskName: localSelectedRisk.name,
        newRiskName: currentSelected
      });

      this.changeActivityRiskClassChecklist(typeName, className);
      this.$store.dispatch('common/setActivitySelectionValid');
      this.$emit('changeActivityDone');
    },
    removeActivitySelectedRiskId() {
      const typeName = this.currentSelectedType.name;
      const className = this.currentSelectedClass.name;

      if (!this.currentSelectedRisk) {
        const selectedClasses = this.selectedActivityClasses[0].types.find(
          (t) => t.name === typeName
        ).classes;
        const selectedClass = selectedClasses.find((c) => c.name === className);
        const index = selectedClasses.indexOf(selectedClass);
        selectedClasses.splice(index, 1);
      } else {
        const riskName = this.currentSelectedRisk.name;

        this.$store.dispatch('common/deleteExcursionSelectedCategoryTypeClassRisk', {
          categoryName: riskCategoryLabel.ACTIVITIES,
          typeName,
          className,
          riskName
        });
        this.changeActivityRiskClassChecklist(typeName, className);
      }

      this.$emit('changeActivityDone');
      this.$store.dispatch('common/setActivitySelectionValid');
      this.currentSelectedType = {};
      this.currentSelectedClass = {};
      this.currentSelectedRisk = {};
      this.deleteActivityRiskDisplayed = false;
      return true;
    },
    resetSelectedClass() {
      this.$store.commit(types.COMMON_SET_SELECTED_ACTIVITY_CLASS_FILTER, null);
    },
    getTicked(item) {
      let allBox = 0;
      let allChecked = 0;
      if (
        item &&
        item.CompleteChecklist &&
        item.CompleteChecklist.length > 0 &&
        item.CompleteChecklist[0].checklist &&
        item.CompleteChecklist[0].checklist.Strategy &&
        item.CompleteChecklist[0].checklist.Strategy.data
      ) {
        const { data } = item.CompleteChecklist[0].checklist.Strategy;
        const dataKey = Object.keys(data);

        if (
          data[dataKey] &&
          data[dataKey].children &&
          data[dataKey].children.length > 0 &&
          data[dataKey].children[0].children
        ) {
          const checklist = data[dataKey].children[0].children;

          if (checklist && checklist.length > 0) {
            checklist.forEach((box) => {
              if (box.children && box.children.length > 0) {
                const boxes = box.children.filter((ck) => {
                  return ck.contentType === 'checkbox';
                });

                if (boxes && boxes.length > 0) {
                  allBox += boxes.length;
                }

                const checked = box.children.filter((ck) => {
                  return ck.ticked && ck.contentType === 'checkbox';
                });

                if (checked && checked.length > 0) {
                  allChecked += checked.length;
                }
              }
            });
          }
        }
      } else {
        allChecked = -1;
      }

      return allBox === allChecked;
    },
    changeActivityRiskClassChecklist(riskTypeName, riskClassName) {
      const risk = this.$store.getters['common/getActivityRiskClass'](riskTypeName, riskClassName);
      const excursionCommonAdapter = new this.$app.excursionCommon.Adapter({
        store: this.$store,
        app: this.$app
      });
      const allParams = excursionCommonAdapter.getActivityParams();

      const params = allParams.filter((p) => p.indexOf(riskClassName) >= 0);
      const queryParam = this.$route.query;
      this.checklistFetchActionRiskTypeName = riskTypeName;
      this.checklistFetchActionRiskClassName = riskClassName;
      this.checklistFetchActionInProgress = true;
      this.$store.commit(types.COMMON_SET_CHECKLIST_FETCH_ACTION_IN_PROGRESS, true);
      this.elementDisabled = true;
      this.checklistFetchActionCount += 1;
      const that = this;
      const argHash = {
        stbApiAdapter: this.$app.stbApiAdapter,
        store: this.$store,
        queryParam,
        params,
        risk,
        resolve() {
          that.checklistFetchActionDoneCount += 1;
          if (that.checklistFetchActionCount === that.checklistFetchActionDoneCount) {
            that.checklistFetchActionInProgress = false;
            that.$store.commit(types.COMMON_SET_CHECKLIST_FETCH_ACTION_IN_PROGRESS, false);
            that.elementDisabled = false;
            that.checklistFetchActionCount = 0;
            that.checklistFetchActionDoneCount = 0;
          }
        }
      };

      this.$store.dispatch('common/changeActivityRiskCategoryCompleteChecklist', argHash);

      if (this.$refs.completeChecklist) {
        const currentButtons = this.$refs.completeChecklist.filter(
          (c) => c.completeChecklistBtnId === `btnChecklist${riskTypeName}${riskClassName}`
        );
        if (currentButtons && currentButtons.length > 0) {
          currentButtons[0].resetCompleteButton();
        }
      }
    },
    displayDeleteRiskConfirm(riskType, riskClass, risk) {
      this.deleteActivityRiskDisplayed = true;
      this.currentSelectedType = riskType;
      this.currentSelectedClass = riskClass;
      this.currentSelectedRisk = risk;
    },
    setKey(riskClass) {
      return riskClass.name.replace(/\s*/g, '');
    },
    getActivityRiskId(riskType, riskClass, selectedRisk) {
      const riskTypeId = riskType.name.replace(/\s*/g, '');
      const riskClassId = riskClass.name.replace(/\s*/g, '');
      const riskId = selectedRisk.name.replace(/\s*/g, '');
      return `activity-risk--${riskTypeId}-${riskClassId}-${riskId}`;
    },
    getActivityRiskDeleteId(riskType, riskClass, selectedRisk) {
      const riskTypeId = riskType.name.replace(/\s*/g, '');
      const riskClassId = riskClass.name.replace(/\s*/g, '');
      const riskId = selectedRisk ? `-${selectedRisk.name.replace(/\s*/g, '')}` : '';
      return `activity-risk--btn-delete-${riskTypeId}-${riskClassId}${riskId}`;
    }
  }
};
</script>

<style scoped>
:deep(.activity-category-class .v-select__selection-text) {
  color: rgb(var(--v-theme-black-lighten-5));
}

:deep(.search-container .mdi-close) {
  color: rgb(var(--v-theme-primary));
}

.sub-category-section {
  color: #818080;
}

.sub-category-section .name {
  font-style: italic;
}

.sub-category-section .section-btn {
  font-size: 0.8em;
}

.active-risk-category-activity :deep(.v-list-item__overlay) {
  background-color: #ffffff;
}

.empty-risk-selection :deep(.v-field) {
  box-shadow:
    0px 3px 1px -2px rgb(29, 175, 236),
    0px 2px 2px 0px rgb(29, 175, 236),
    0px 1px 5px 0px rgb(29, 175, 236) !important;
}

.active-risk-category-activity.disabled-risk {
  opacity: 1;
}

.disabled-risk {
  opacity: 0.4;
}
</style>
